"use client";
import { thumbUrl } from "@/lib/images";

import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import {
  Compass,
  Eye,
  EyeOff,
  Glasses,
  HelpCircle,
  Map as MapIcon,
  Maximize,
  Minimize,
  Minus,
  Plus,
  X,
} from "lucide-react";
import type { Scene } from "@/lib/types";
import type { PanoViewerHandle } from "./PanoViewer";
import { getCategory, getScene, nearbyScenes } from "@/lib/scenes";
import { displayCommune, displayTitle } from "@/lib/editorial";
import { BASE_PATH } from "@/lib/base-path";

// krpano n'entre jamais dans le bundle initial. Pas de squelette ici :
// le voile de fondu d'entrée (ci-dessous) couvre déjà toute la phase de
// chargement, avec son loader différé.
const PanoViewer = dynamic(() => import("./PanoViewer"), {
  ssr: false,
  loading: () => <div className="absolute inset-0 bg-basalte" aria-hidden />,
});

// L'API WebXR n'est pas dans les types DOM de TS 5.9
declare global {
  interface Navigator {
    xr?: { isSessionSupported: (mode: string) => Promise<boolean> };
  }
}

const btn =
  "grid h-11 w-11 place-items-center rounded-full text-corail transition-colors hover:bg-corail/10 focus-visible:bg-corail/10";

/**
 * Lecteur 360° complet : krpano + interface en surimpression masquable
 * (touche H ou bouton), zoom, plein écran, gyroscope si disponible,
 * VR uniquement si WebXR est réellement détecté, liste « à proximité ».
 */
export default function ExperienceViewer({ scene: initial }: { scene: Scene }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<PanoViewerHandle | null>(null);
  const [scene, setScene] = useState(initial);
  const [uiVisible, setUiVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [gyroAvailable, setGyroAvailable] = useState(false);
  const [gyroOn, setGyroOn] = useState(false);
  const [vrAvailable, setVrAvailable] = useState(false);
  const [tooltip, setTooltip] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // false tant que krpano n'a pas fini de charger le premier panorama :
  // un voile basalte couvre le viewer (aucun écran blanc), puis fondu 500 ms
  const [panoReady, setPanoReady] = useState(false);

  // Détections de capacités — le bouton VR n'apparaît que si WebXR répond
  // réellement ; le gyroscope que si l'API existe (permission au premier tap).
  useEffect(() => {
    if (typeof window.DeviceOrientationEvent !== "undefined" && "ontouchstart" in window) {
      setGyroAvailable(true);
    }
    navigator.xr
      ?.isSessionSupported("immersive-vr")
      .then((ok) => setVrAvailable(ok))
      .catch(() => setVrAvailable(false));
  }, []);

  // Masquage de l'interface : touche H
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "h" || e.key === "H") setUiVisible((v) => !v);
      if (e.key === "Escape") setHelpOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const changeScene = useCallback((slug: string) => {
    const next = getScene(slug);
    if (!next) return;
    setScene(next);
    // URL et titre mis à jour sans remonter le composant (le fondu krpano reste fluide)
    window.history.replaceState(null, "", `${BASE_PATH}/experience/${slug}/`);
    document.title = `${displayTitle(next)} - Lecteur 360° - La Réunion en 360°`;
  }, []);

  const showToast = useCallback((title: string) => {
    setToast(`« ${title} » - scène bientôt disponible.`);
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void wrapperRef.current?.requestFullscreen();
  }, []);

  const category = getCategory(scene.category);
  const nearby = nearbyScenes(scene, 2);

  if (error) {
    return (
      <div className="grid h-dvh place-items-center bg-basalte p-6">
        <div className="max-w-md text-center">
          <Image
            src={thumbUrl(scene.slug)}
            alt={`Aperçu statique : ${scene.title}`}
            width={400}
            height={200}
            className="mx-auto rounded-xl"
          />
          <p className="mt-5 text-corail">Le panorama n&apos;a pas pu être chargé.</p>
          <p className="mt-1.5 text-sm text-mineral">{error}</p>
          <Link
            href={`/lieux/${scene.slug}/`}
            className="mt-4 inline-block text-lagon hover:text-or"
          >
            ← Retour à la fiche
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative h-dvh overflow-hidden bg-basalte">
      <PanoViewer
        scene={initial}
        onSceneChange={changeScene}
        onDisabledHotspot={showToast}
        onHotspotHover={setTooltip}
        onReady={(h) => {
          handleRef.current = h;
          // filet de sécurité : si onloadcomplete n'arrivait pas, on révèle
          // quand même le panorama plutôt que de laisser le voile en place
          window.setTimeout(() => setPanoReady(true), 2500);
        }}
        onPanoLoaded={() => setPanoReady(true)}
        onError={setError}
      />

      {/* Fondu d'entrée : voile basalte pendant le chargement, aucun écran
          blanc ; le loader n'apparaît que si le panorama tarde (~450 ms) */}
      <div
        aria-hidden={panoReady}
        className={`absolute inset-0 z-20 bg-basalte transition-opacity duration-500 ease-out ${
          panoReady ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        {!panoReady && (
          <div
            role="status"
            className="appear-late absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 text-sm text-mineral"
          >
            <span
              className="h-5 w-5 animate-spin rounded-full border-2 border-mineral/30 border-t-or"
              aria-hidden
            />
            Chargement du panorama…
          </div>
        )}
      </div>

      {/* Infobulle hotspot */}
      {tooltip && (
        <div className="pointer-events-none absolute top-20 left-1/2 z-20 -translate-x-1/2 rounded-lg border border-white/10 bg-basalte/85 px-3.5 py-1.5 text-[13px] backdrop-blur-sm">
          {tooltip}
        </div>
      )}

      {/* Toast scène hors prototype */}
      {toast && (
        <div
          role="status"
          className="absolute bottom-24 left-1/2 z-30 w-max max-w-[85vw] -translate-x-1/2 rounded-lg border border-white/10 bg-panneau px-4 py-2.5 text-[13px] shadow-xl"
        >
          {toast}
        </div>
      )}

      {uiVisible && (
        <>
          {/* Bandeau haut : titre + retour */}
          <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between bg-gradient-to-b from-basalte/70 to-transparent p-5">
            <div>
              <p className="text-[11px] tracking-[0.14em] text-lagon uppercase">
                {displayCommune(scene)}
                {category && category.id !== "a_classer" ? ` - ${category.label}` : ""}
              </p>
              <h1 className="font-serif text-[22px] font-normal">{displayTitle(scene)}</h1>
            </div>
            <Link
              href={`/lieux/${scene.slug}/`}
              className="rounded-full border border-corail/20 bg-basalte/60 px-4.5 py-2.5 text-xs font-medium backdrop-blur-sm transition-colors hover:border-or hover:text-or"
            >
              ✕&nbsp; Quitter
            </Link>
          </div>

          {/* À proximité */}
          {nearby.length > 0 && (
            <aside className="absolute right-5 bottom-24 z-10 hidden w-52 rounded-2xl border border-white/10 bg-basalte/65 p-3.5 backdrop-blur-md md:block">
              <h2 className="mb-1.5 text-[11px] tracking-[0.12em] text-mineral uppercase">
                À proximité
              </h2>
              {nearby.map((n) => (
                <button
                  key={n.slug}
                  onClick={() => {
                    handleRef.current?.loadScene(n.slug);
                    changeScene(n.slug);
                  }}
                  className="group flex w-full items-center gap-2.5 py-1.5 text-left"
                >
                  <Image
                    src={thumbUrl(n.slug)}
                    alt=""
                    width={56}
                    height={38}
                    className="h-9 w-14 rounded-md object-cover"
                  />
                  <span>
                    <b className="block text-[12.5px] font-normal group-hover:text-or">
                      {displayTitle(n)}
                    </b>
                    <small className="text-[10.5px] text-mineral">
                      {n.distanceKm.toFixed(1).replace(".", ",")} km
                    </small>
                  </span>
                </button>
              ))}
            </aside>
          )}

          {/* Barre d'outils */}
          <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full border border-corail/15 bg-basalte/60 p-1.5 backdrop-blur-md">
            <Link
              href={`/carte/?lieu=${scene.slug}`}
              className={btn}
              aria-label="Voir sur la carte"
              title="Carte"
            >
              <MapIcon className="h-[18px] w-[18px]" aria-hidden />
            </Link>
            <button
              onClick={() => handleRef.current?.zoomOut()}
              className={btn}
              aria-label="Zoom arrière"
              title="Zoom arrière"
            >
              <Minus className="h-[18px] w-[18px]" aria-hidden />
            </button>
            <button
              onClick={() => handleRef.current?.zoomIn()}
              className={btn}
              aria-label="Zoom avant"
              title="Zoom avant"
            >
              <Plus className="h-[18px] w-[18px]" aria-hidden />
            </button>
            <button
              onClick={toggleFullscreen}
              className={btn}
              aria-label={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
              title="Plein écran"
            >
              {isFullscreen ? (
                <Minimize className="h-[18px] w-[18px]" aria-hidden />
              ) : (
                <Maximize className="h-[18px] w-[18px]" aria-hidden />
              )}
            </button>
            {gyroAvailable && (
              <button
                onClick={() => {
                  const next = !gyroOn;
                  setGyroOn(next);
                  handleRef.current?.toggleGyro(next);
                }}
                className={`${btn} ${gyroOn ? "text-or" : ""}`}
                aria-label={gyroOn ? "Désactiver le gyroscope" : "Activer le gyroscope"}
                aria-pressed={gyroOn}
                title="Gyroscope"
              >
                <Compass className="h-[18px] w-[18px]" aria-hidden />
              </button>
            )}
            {vrAvailable && (
              <button
                onClick={() => handleRef.current?.enterVr()}
                className={btn}
                aria-label="Entrer en réalité virtuelle"
                title="Réalité virtuelle"
              >
                <Glasses className="h-[18px] w-[18px]" aria-hidden />
              </button>
            )}
            <button
              onClick={() => setHelpOpen(true)}
              className={btn}
              aria-label="Aide"
              title="Aide"
            >
              <HelpCircle className="h-[18px] w-[18px]" aria-hidden />
            </button>
            <button
              onClick={() => setUiVisible(false)}
              className={btn}
              aria-label="Masquer l'interface"
              title="Masquer l'interface (H)"
            >
              <EyeOff className="h-[18px] w-[18px]" aria-hidden />
            </button>
          </div>
        </>
      )}

      {!uiVisible && (
        <button
          onClick={() => setUiVisible(true)}
          className="absolute right-5 bottom-5 z-10 grid h-11 w-11 place-items-center rounded-full border border-corail/15 bg-basalte/50 text-corail backdrop-blur-md"
          aria-label="Afficher l'interface"
          title="Afficher l'interface (H)"
        >
          <Eye className="h-[18px] w-[18px]" aria-hidden />
        </button>
      )}

      {/* Aide */}
      {helpOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Aide à la navigation"
          className="absolute inset-0 z-30 grid place-items-center bg-basalte/80 p-6 backdrop-blur-sm"
        >
          <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-panneau p-6">
            <button
              onClick={() => setHelpOpen(false)}
              className="absolute top-3.5 right-3.5 grid h-9 w-9 place-items-center rounded-full text-mineral hover:text-or"
              aria-label="Fermer l'aide"
            >
              <X className="h-4.5 w-4.5" aria-hidden />
            </button>
            <h2 className="font-serif text-lg">Comment naviguer</h2>
            <ul className="mt-3 space-y-2 text-[13.5px] text-corail/85">
              <li>- Glissez (ou flèches du clavier) pour regarder autour de vous.</li>
              <li>- Molette ou boutons +/− pour zoomer.</li>
              <li>- Les cercles dorés mènent aux panoramas voisins.</li>
              <li>- Touche H : masquer ou afficher l&apos;interface.</li>
              <li>- Les cercles gris signalent des scènes pas encore migrées.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
